import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import MosSmartDialog from '../MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { previewBatchFinish, finishBatch } from './batchApi'
import { buildBatchFinishRequest } from './batchContract'
import BatchReasonsList, { translateBatchRejectionMessage } from './batchReasons'
import BatchBlockedNotice from './BatchBlockedNotice'
import ActiveDeviceRow from '../ActiveDeviceRow'
import { BATCH_TABLE_CONTAINER_STYLE } from './batchTable'

// Historia 120 (Jupiter, paquete P8) -- S160, S162, S163, S164, S165, S166 built one scenario at a
// time (`log.md`, cabecera "Jupiter P8"), plus the gesture-wide 400 rejections of N4/N5
// (`BatchNoLongerActive`/`BatchSelectionEmpty`/`BatchDuplicateJob`), painted with the SAME
// `translateBatchRejectionMessage` every other batch surface uses -- no literal whitelist
// (§7.2 #15-ter, D-SP20).
//
// D-SP7/D-SP14 -- `BLOCKED` is rendered by the SAME `BatchBlockedNotice` N3's pause already uses,
// never a second renderer. Getting `BLOCKED` implies ZERO writes and ZERO state changes (D-I4/RN44):
// the handler below touches only `blockedJobs`, never `preview`/`result` -- what the operator was
// looking at keeps showing exactly what N4 said.
//
// D-I9/RN48 -- `CONFIRMATION_REQUIRED` asks ONCE for the whole batch (never per job), reusing the
// SAME title/message the individual finish already uses today (`JobStatusChangeDialog.jsx`), with
// `count` = the BATCH total (`incompletePartsToReportNok`). Confirming resends the exact same
// gesture with `confirmReportIncompleteAsNok: true` (RN48.1 -- not a bypass, Sun still judges it).

const READY = 'READY'

/**
 * 🔴 S260/D-SP30 (ENMIENDA 10) -- CÓMO SE ELIGE LA PREGUNTA, y por qué NO por `type` a secas.
 *
 * El PO se encontró un BUCLE SIN FIN: al cerrar un lote con un job abierto en otra máquina salía
 * *"Finish with incomplete parts? — 0 parts…"*, y al aceptar volvía a salir. La causa estaba aquí:
 * se ramificaba solo por `type` y se rellenaba el contador con `?? 0`. Ahora hay DOS preguntas con el
 * mismo `type` y, en la multi-máquina, `incompletePartsToReportNok` **no viaja** — así que el `?? 0`
 * **inventaba el cero**, pintaba la pregunta equivocada y la respuesta hacía que Sun volviera a
 * preguntar lo mismo.
 *
 * 🔒 REGLA, POR PRESENCIA PRIMERO:
 *   1. `incompletePartsToReportNok` PRESENTE ⇒ piezas NOK (lleve o no `reason`) — así sigue
 *      funcionando aunque un Sun futuro deje de mandar `reason` en esa pregunta.
 *   2. Si no, `reason === 'JOB_ACTIVE_IN_OTHER_DEVICES'` ⇒ diálogo de otras máquinas.
 *   3. Si no ⇒ **error VISIBLE y NINGUNA petición más**. **La ausencia del campo nunca es cero.**
 *
 * ⚠️ Diverge A PROPÓSITO del cierre individual (D-SP30), que trata lo desconocido como la pregunta de
 * piezas para hablar con un Sun ANTIGUO sin `reason`. N5 nace en esta historia: no hay Sun antiguo con
 * el que ser compatible, y lo que no puede hacer este diálogo es INVENTARSE una pregunta.
 */
const OTHER_DEVICES_REASON = 'JOB_ACTIVE_IN_OTHER_DEVICES'

const hasIncompletePartsQuestion = (response) =>
  Object.prototype.hasOwnProperty.call(response ?? {}, 'incompletePartsToReportNok') &&
  response.incompletePartsToReportNok !== null &&
  response.incompletePartsToReportNok !== undefined
const EMPTY_PREVIEW = { jobs: [], totalPartsToReportNok: 0, interruptInOtherDevicesRequired: false }
const EMPTY_RESULT = { finished: [], warnings: [] }
const EMPTY_CONFIRMATION = { open: false, count: 0 }
const CLOSED_OTHER_DEVICES = { open: false, jobs: [] }

const jobKey = (job) => `${job?.fabricationOrderId}-${job?.jobReferenceId}`

const findJobData = (jobs, pair) => jobs.find((job) => job.fabricationOrderId === pair.fabricationOrderId && job.jobReferenceId === pair.jobReferenceId) ?? pair

/**
 * S160/S240 (ENMIENDA 07, G3-11 a) -- una FILA DE TABLA por job, con sus cinco columnas en CELDAS:
 * orden de fabricación · referencia de pieza · operación · reportado · pendiente, más la celda de
 * motivos en la sección de los que aún no pueden finalizarse (S161).
 *
 * 🔴 **POR QUÉ CADA CELDA LLEVA EL VALOR SUELTO Y NUNCA DENTRO DE UNA PLANTILLA DE TEXTO:** la versión
 * anterior componía `${t_('Fabrication order')}: ${job.fabricationOrderCode} — …`, y un nulo
 * interpolado **se imprime como la palabra "null"**. El PO lo vio en la aplicación: cinco filas
 * diciendo *"Fabrication order: null — Reference: null"*. Puesto como hijo de JSX, un `null` o un
 * `undefined` **no pinta nada**, que es exactamente lo que RN69/POL7 pide: campo ausente ⇒
 * comportamiento seguro. ⚠️ Esta garantía **no depende de que Sun rellene los códigos** (eso es S241,
 * de Sun): sigue siendo exigible aunque los rellene.
 */
const machineNames = (job) =>
  (Array.isArray(job?.activeDevices) ? job.activeDevices : []).map((device) => device?.name).filter(Boolean)

const JobPreviewTable = ({ jobs, withReasons = false, withMachines = false, t_ }) => (
  <TableContainer style={BATCH_TABLE_CONTAINER_STYLE}>
    <Table size='small' stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>{t_('Fabrication order')}</TableCell>
          <TableCell>{t_('Reference')}</TableCell>
          <TableCell>{t_('Operation')}</TableCell>
          <TableCell>{t_('Reported')}</TableCell>
          <TableCell>{t_('Pending')}</TableCell>
          {/* S255: la columna solo existe cuando la respuesta trae la decisión pendiente -- con la
              marca en falso, el diálogo es el de siempre y sus cinco columnas (S160). */}
          {withMachines && <TableCell>{t_('Machine')}</TableCell>}
          {withReasons && <TableCell>{t_('Reason')}</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs.map((job) => (
          <TableRow key={jobKey(job)} hover>
            <TableCell>{job.fabricationOrderCode}</TableCell>
            <TableCell>{job.partReferenceCode}</TableCell>
            <TableCell>{job.nodeName}</TableCell>
            <TableCell>{job.reportedQuantity}</TableCell>
            <TableCell>{job.pendingQuantity}</TableCell>
            {/* S255 -- las máquinas del job, con su NOMBRE, tal como vienen en `activeDevices` de la
                MISMA respuesta de N4 que abrió el diálogo: ni una petición por fila. */}
            {withMachines && <TableCell>{machineNames(job).join(', ')}</TableCell>}
            {/* S161 -- el MISMO renderizador de motivos que las demás superficies (§7.2 #15-bis):
                vivir en una celda no autoriza un segundo traductor ni recortar el texto. */}
            {withReasons && <TableCell><BatchReasonsList reasons={job.reasons} /></TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

JobPreviewTable.propTypes = {
  jobs: PropTypes.arrayOf(PropTypes.shape({
    fabricationOrderId: PropTypes.number,
    jobReferenceId: PropTypes.number,
    fabricationOrderCode: PropTypes.string,
    partReferenceCode: PropTypes.string,
    nodeName: PropTypes.string,
    reportedQuantity: PropTypes.number,
    pendingQuantity: PropTypes.number,
    readiness: PropTypes.string,
    reasons: PropTypes.arrayOf(PropTypes.string)
  })).isRequired,
  withReasons: PropTypes.bool,
  withMachines: PropTypes.bool,
  t_: PropTypes.func.isRequired
}

/**
 * Historia 120 (Jupiter, paquete P8) -- the preview and the bulk finish of a device's active batch
 * (HU-06, CU11+CU12). Consumes N4+N5 and no other route.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {number} props.deviceId
 * @param {number} props.workerId
 * @param {string} props.batchId
 * @param {Function} props.onClose - Called with no arguments whenever the operator dismisses the
 *   dialog.
 * @param {Function} props.onFinished - Called with the raw N5 `SUCCESS` response once it succeeds.
 */
const BatchFinishDialog = ({ open = false, deviceId, workerId, batchId, onClose = () => {}, onFinished = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [phase, setPhase] = useState('preview')
  const [preview, setPreview] = useState(EMPTY_PREVIEW)
  const [result, setResult] = useState(EMPTY_RESULT)
  const [blockedJobs, setBlockedJobs] = useState([])
  const [confirmation, setConfirmation] = useState(EMPTY_CONFIRMATION)
  const [otherDevices, setOtherDevices] = useState(CLOSED_OTHER_DEVICES)
  const [rejection, setRejection] = useState(null)
  // S257 -- una vez concedida, la confirmación de otras máquinas acompaña a las siguientes peticiones
  // del MISMO gesto. Va en ref y no en estado porque `submitFinish` la lee en el instante del envío.
  const otherDevicesConfirmedRef = useRef(false)
  // Doble clic (issue #2964): cada pulsación era un `finishBatch`. Se libera cuando la petición se
  // asienta, NO al cerrar el diálogo, porque las dos reentradas legítimas del gesto
  // (`handleConfirmNok`, `handleConfirmOtherDevices`) nacen justo en el `.then` de la anterior y
  // tienen que poder enviar la suya.
  const submitInFlightRef = useRef(false)
  const blocked = blockedJobs.length > 0

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPhase('preview')
    setResult(EMPTY_RESULT)
    setBlockedJobs([])
    setConfirmation(EMPTY_CONFIRMATION)
    setOtherDevices(CLOSED_OTHER_DEVICES)
    otherDevicesConfirmedRef.current = false
    submitInFlightRef.current = false
    setRejection(null)
    setPreview(EMPTY_PREVIEW)

    previewBatchFinish({ deviceId, workerId, batchId })
      .then((response) => {
        if (cancelled) return
        setPreview({
          jobs: Array.isArray(response?.jobs) ? response.jobs : [],
          totalPartsToReportNok: response?.totalPartsToReportNok ?? 0,
          // S255: el aviso de DECISIÓN PENDIENTE. No es un impedimento -- los jobs siguen `READY` y el
          // botón de confirmar sigue disponible; inhabilitarlo sería volver al bloqueo que la
          // enmienda 10 retira.
          interruptInOtherDevicesRequired: response?.interruptInOtherDevicesRequired === true
        })
      })
      .catch((error) => {
        if (cancelled) return
        setRejection(error)
        setPhase('rejected')
      })

    return () => { cancelled = true }
  }, [open, deviceId, workerId, batchId])

  // N5 -- SUCCESS/CONFIRMATION_REQUIRED/BLOCKED; gesture-wide rejections are a later cycle.
  const submitFinish = useCallback((confirmReportIncompleteAsNok, interruptInOtherDevicesConfirmed) => {
    if (submitInFlightRef.current) return Promise.resolve()
    submitInFlightRef.current = true
    // S259/D-SP28 -- las dos banderas viajan SIEMPRE explícitas: el cuerpo es idéntico campo a campo
    // entre las tres peticiones del gesto, y lo único que cambia entre ellas es su valor.
    const request = buildBatchFinishRequest({
      deviceId,
      workerId,
      batchId,
      jobs: preview.jobs,
      confirmReportIncompleteAsNok,
      interruptInOtherDevicesConfirmed
    })

    return finishBatch(request).then((response) => {
      if (response?.type === 'SUCCESS') {
        setResult({ finished: Array.isArray(response.finished) ? response.finished : [], warnings: Array.isArray(response.warnings) ? response.warnings : [] })
        setPhase('success')
        onFinished(response)
      } else if (response?.type === 'CONFIRMATION_REQUIRED') {
        // 🔴 S260/D-SP30 -- POR PRESENCIA PRIMERO, y NUNCA por defecto a cero. Ver la nota de
        // `hasIncompletePartsQuestion`: preguntar lo que no es produjo un bucle sin fin en planta.
        if (hasIncompletePartsQuestion(response)) {
          // D-I9/RN48 -- ONE question for the whole batch; NOTHING is written yet.
          setConfirmation({ open: true, count: response.incompletePartsToReportNok })
        } else if (response.reason === OTHER_DEVICES_REASON) {
          // S256 -- UN solo diálogo para todo el gesto, nunca uno por job: un consentimiento parcial
          // no cabe en un todo-o-nada (RN43).
          setOtherDevices({ open: true, jobs: Array.isArray(response.jobs) ? response.jobs : [] })
        } else {
          // 🔴 S260 -- una pregunta que este cliente NO CONOCE es un ERROR VISIBLE, no una excusa para
          // elegir cualquiera de las dos: **cero peticiones más**. Se pinta por el mismo camino que los
          // rechazos de N4/N5, con `An error occurred`, que ya existe en los 8 idiomas — cero claves
          // nuevas. 🚫 Caer en la pregunta de piezas "porque es la que había" es exactamente el bucle
          // que el PO sufrió, y con un `reason` futuro volvería a pasar en silencio.
          setRejection({ message: 'An error occurred' })
          setPhase('rejected')
        }
      } else if (response?.type === 'BLOCKED') {
        // D-I4/RN44 -- `phase`/`preview`/`result` are deliberately left untouched: the dialog
        // underneath keeps showing exactly what N4 said, nothing here got marked paused/finished.
        setBlockedJobs(Array.isArray(response.jobs) ? response.jobs : [])
      }
    }).catch((error) => {
      setRejection(error)
      setPhase('rejected')
    }).finally(() => {
      submitInFlightRef.current = false
    })
  }, [deviceId, workerId, batchId, preview.jobs, onFinished])

  const handleConfirmFinish = useCallback(() => submitFinish(false, false), [submitFinish])

  const handleConfirmNok = useCallback(() => {
    setConfirmation(EMPTY_CONFIRMATION)
    // S163/S257 -- the SAME gesture, resent with the confirmation flag set (RN48.1: Sun still judges
    // it). Si ya se concedió la de otras máquinas, VIAJA PUESTA en esta tercera: cada confirmación
    // arrastra las anteriores.
    submitFinish(true, otherDevicesConfirmedRef.current)
  }, [submitFinish])

  // S257 -- decir que sí a las máquinas emite EXACTAMENTE una petición más, con el mismo cuerpo salvo
  // la bandera; el diálogo no se vuelve a abrir en este gesto.
  const handleConfirmOtherDevices = useCallback(() => {
    setOtherDevices(CLOSED_OTHER_DEVICES)
    otherDevicesConfirmedRef.current = true
    submitFinish(false, true)
  }, [submitFinish])

  // S256 -- decir que NO es, en el cable, no volver a llamar: el servidor no recibe una negativa,
  // recibe una llamada menos. Nada que limpiar y ningún error que mostrar.
  const handleCancelOtherDevices = useCallback(() => setOtherDevices(CLOSED_OTHER_DEVICES), [])

  const handleCancelNok = useCallback(() => {
    // S163 -- cancelling the single question leaves the batch exactly as it was before the gesture.
    setConfirmation(EMPTY_CONFIRMATION)
  }, [])

  const handleBlockedClose = useCallback(() => {
    // S164 -- nothing was written, so closing the notice needs no cleanup: hand control straight
    // back to the caller.
    onClose()
  }, [onClose])

  const readyJobs = preview.jobs.filter((job) => job.readiness === READY)
  const notReadyJobs = preview.jobs.filter((job) => job.readiness !== READY)

  return (
    <>
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: t_('Finish batch'), icon: DoneAllRoundedIcon })}
        setOpen={open}
        cancelCallback={onClose}
        accept={phase === 'preview'}
        acceptLabel='Confirm'
        acceptCallback={handleConfirmFinish}
        maxPaperWidth='50rem'
      >
        <Stack spacing={2}>
          {phase === 'rejected' && (
            <Typography color='error'>{translateBatchRejectionMessage(t_, rejection)}</Typography>
          )}

          {phase === 'preview' && (
            <>
              {/* S255 -- AVISO de decisión pendiente, no de impedimento: reutiliza el título que el
                  cierre individual ya usa para lo mismo (D-SP27, cero claves nuevas). */}
              {preview.interruptInOtherDevicesRequired && (
                <Typography color='warning.main' fontWeight='bold'>{t_('Job active in other machines')}</Typography>
              )}
              {/* CA6.2 -- la separación entre lo que se va a finalizar y lo que aún no puede se
                  MANTIENE. ⚠️ Y sus epígrafes siguen SIN recuento: las secciones recontadas son de la
                  previsualización de INICIO (S116/S235) y el PO decidió no traerlas aquí. */}
              {readyJobs.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant='subtitle1' fontWeight='bold'>{t_('Jobs to finish')}</Typography>
                  <JobPreviewTable jobs={readyJobs} withMachines={preview.interruptInOtherDevicesRequired} t_={t_} />
                </Stack>
              )}
              {notReadyJobs.length > 0 && (
                <Stack spacing={1}>
                  <Divider />
                  <Typography variant='subtitle1' fontWeight='bold'>{t_('Jobs not ready to finish')}</Typography>
                  <JobPreviewTable jobs={notReadyJobs} withReasons t_={t_} />
                </Stack>
              )}
              {preview.totalPartsToReportNok > 0 && (
                <Typography variant='body2' color='warning.main'>
                  {`${t_('NOK')}: ${preview.totalPartsToReportNok}`}
                </Typography>
              )}
            </>
          )}

          {phase === 'success' && (
            <>
              {result.finished.map((pair) => {
                const jobData = findJobData(preview.jobs, pair)
                return (
                  <Stack key={jobKey(pair)} direction='row' spacing={1} alignItems='center'>
                    <CheckCircleRoundedIcon color='success' fontSize='small' />
                    <Typography variant='body2'>
                      {`${t_('Fabrication order')}: ${jobData.fabricationOrderCode ?? pair.fabricationOrderId} — ${t_('Reference')}: ${jobData.partReferenceCode ?? ''} — ${t_('Operation')}: ${jobData.nodeName ?? ''} — ${t_('Finished')}`}
                    </Typography>
                  </Stack>
                )
              })}
              <BatchReasonsList reasons={result.warnings} />
            </>
          )}
        </Stack>
      </MosSmartDialog>

      {/* 🔒 S256/S258/D-SP27 -- UN diálogo para todo el gesto, con las claves QUE YA EXISTEN: título e
          `Interrupt and finish` son los del cierre individual de #2935, y `Cancel` el de siempre.
          ⚠️ SIN frase de cuerpo a propósito: la del individual está en SINGULAR ("This job…") y aquí se
          listan varios jobs — LA LISTA ES EL CUERPO, que es lo que S256 exige mostrar. Inventar una
          redacción en plural habría sido una clave nueva y una decisión de GATE 3. */}
      <MosSmartDialog
        title={createDialogTitle({ color: 'warning', title: t_('Job active in other machines'), icon: PauseCircleOutlineIcon })}
        setOpen={open && otherDevices.open}
        cancel
        accept
        cancelLabel='Cancel'
        acceptLabel='Interrupt and finish'
        cancelCallback={handleCancelOtherDevices}
        acceptCallback={handleConfirmOtherDevices}
      >
        <Stack spacing={1} sx={{ px: 3, pb: 1, textAlign: 'left' }}>
          {otherDevices.jobs.map((job) => {
            const jobData = findJobData(preview.jobs, job)
            return (
              <Stack key={jobKey(job)} spacing={0.25}>
                {/* Cada job se identifica como en el resto del diálogo de cierre: su orden y su job. */}
                <Typography variant='subtitle2' fontWeight='bold'>
                  {[jobData.fabricationOrderCode, jobData.nodeName].filter(Boolean).join(' · ')}
                </Typography>
                {/* ENMIENDA 12 (G3-17) -- LA MISMA FILA QUE EL CIERRE INDIVIDUAL: nombre, modo,
                    operario y desde cuándo. Los cuatro datos YA VIAJAN en la respuesta de N5 (D-SP29),
                    que es la fuente porque refleja el estado en el momento del gesto: cero contrato,
                    cero peticiones y cero claves nuevas. Se MONTA la fila compartida, no se copia. */}
                {(Array.isArray(job.activeDevices) ? job.activeDevices : []).map((device) => (
                  <ActiveDeviceRow key={device.id} device={device} />
                ))}
              </Stack>
            )
          })}
        </Stack>
      </MosSmartDialog>

      <MosSmartDialog
        title={createDialogTitle({ color: 'warning', title: t_('Finish with incomplete parts?'), icon: WarningAmberRoundedIcon })}
        setOpen={open && confirmation.open}
        cancelCallback={handleCancelNok}
        acceptCallback={handleConfirmNok}
        message={t_('{{count}} parts do not have their material fully consumed. Report them as NOT OK and finish?', { count: confirmation.count })}
      />

      <BatchBlockedNotice
        open={open && blocked}
        jobs={blockedJobs}
        onClose={handleBlockedClose}
        title={t_('No job of the batch can be finished yet')}
      />
    </>
  )
}

BatchFinishDialog.propTypes = {
  open: PropTypes.bool,
  deviceId: PropTypes.number,
  workerId: PropTypes.number,
  batchId: PropTypes.string,
  onClose: PropTypes.func,
  onFinished: PropTypes.func
}

export default BatchFinishDialog
